import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Download, Factory, Truck, Users, Sprout } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { getProcurementData } from '@/services/reportService';
import type { Procurement } from '@/types/database/procurement';

// Extended type to include source_name from enriched data
interface EnrichedProcurement extends Procurement {
  source_name?: string;
}

interface SupplySourceData {
  sourceType: string;
  totalValue: number;
  totalQuantity: number;
  procurementCount: number;
  avgProcessingDays: number;
  completionRate: number;
  rejectionRate: number;
}

interface SourceBreakdown {
  sourceName: string;
  sourceType: string;
  totalValue: number;
  totalQuantity: number;
  procurementCount: number;
  completionRate: number;
}

const SupplySourceReport = () => {
  const [timeRange, setTimeRange] = useState('3m');
  const [procurements, setProcurements] = useState<EnrichedProcurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [supplySourceData, setSupplySourceData] = useState<SupplySourceData[]>([]);
  const [sourceBreakdown, setSourceBreakdown] = useState<SourceBreakdown[]>([]);

  const sourceTypeIcons = {
    harvest: Sprout,
    supplier: Factory,
    agent: Users,
    farm: Truck
  };

  const sourceTypeColors = {
    harvest: '#10b981',
    supplier: '#3b82f6', 
    agent: '#f59e0b',
    farm: '#8b5cf6'
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getProcurementData(timeRange);
        setProcurements(data as EnrichedProcurement[]);
        processSupplySourceData(data as EnrichedProcurement[]);
      } catch (error) {
        console.error('Error fetching supply source data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  const processSupplySourceData = (data: EnrichedProcurement[]) => {
    // Group by source type
    const sourceTypeMap = new Map<string, SupplySourceData>();
    const sourceMap = new Map<string, SourceBreakdown>();

    // Calculate total procurement value for percentage calculations
    const totalProcurementValue = data.reduce((sum, procurement) => sum + (procurement.total_amount || 0), 0);

    data.forEach(procurement => {
      const sourceType = procurement.source_type || 'unknown';
      
      // Process source type aggregation
      if (!sourceTypeMap.has(sourceType)) {
        sourceTypeMap.set(sourceType, {
          sourceType,
          totalValue: 0,
          totalQuantity: 0,
          procurementCount: 0,
          avgProcessingDays: 0,
          completionRate: 0,
          rejectionRate: 0
        });
      }

      const sourceData = sourceTypeMap.get(sourceType)!;
      sourceData.totalValue += procurement.total_amount || 0;
      sourceData.totalQuantity += procurement.total_quantity || 0;
      sourceData.procurementCount += 1;

      // Process individual source breakdown
      const sourceName = procurement.source_name || `${sourceType}-${procurement.source_id}`;
      const sourceKey = `${sourceType}-${sourceName}`;
      
      if (!sourceMap.has(sourceKey)) {
        sourceMap.set(sourceKey, {
          sourceName,
          sourceType,
          totalValue: 0,
          totalQuantity: 0,
          procurementCount: 0,
          completionRate: 0
        });
      }

      const breakdown = sourceMap.get(sourceKey)!;
      breakdown.totalValue += procurement.total_amount || 0;
      breakdown.totalQuantity += procurement.total_quantity || 0;
      breakdown.procurementCount += 1;
    });

    // Calculate completion and rejection rates
    sourceTypeMap.forEach((sourceData, sourceType) => {
      const sourceTypeProcurements = data.filter(p => p.source_type === sourceType);
      const completed = sourceTypeProcurements.filter(p => p.status === 'completed').length;
      const rejected = sourceTypeProcurements.filter(p => p.status === 'rejected').length;
      
      sourceData.completionRate = sourceTypeProcurements.length > 0 ? (completed / sourceTypeProcurements.length) * 100 : 0;
      sourceData.rejectionRate = sourceTypeProcurements.length > 0 ? (rejected / sourceTypeProcurements.length) * 100 : 0;
    });

    sourceMap.forEach((breakdown, key) => {
      const sourceBreakdownProcurements = data.filter(p => 
        p.source_type === breakdown.sourceType && 
        (p.source_name || `${p.source_type}-${p.source_id}`) === breakdown.sourceName
      );
      const completed = sourceBreakdownProcurements.filter(p => p.status === 'completed').length;
      breakdown.completionRate = sourceBreakdownProcurements.length > 0 ? (completed / sourceBreakdownProcurements.length) * 100 : 0;
    });

    setSupplySourceData(Array.from(sourceTypeMap.values()));
    setSourceBreakdown(Array.from(sourceMap.values()).sort((a, b) => b.totalValue - a.totalValue));
  };

  const handleExport = () => {
    const csvData = supplySourceData.map(item => ({
      'Source Type': item.sourceType,
      'Total Value': item.totalValue,
      'Total Quantity': item.totalQuantity,
      'Procurement Count': item.procurementCount,
      'Completion Rate (%)': item.completionRate.toFixed(2),
      'Rejection Rate (%)': item.rejectionRate.toFixed(2)
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `supply-source-report-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">Supply Source Report</h2>
            <p className="text-sm text-muted-foreground">Procurement performance by supply sources</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-8 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Supply Source Report</h2>
          <p className="text-sm text-muted-foreground">Procurement performance by supply sources</p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1m">Last Month</SelectItem>
              <SelectItem value="3m">Last 3 Months</SelectItem>
              <SelectItem value="6m">Last 6 Months</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExport} size="sm" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {supplySourceData.map((data) => {
          const Icon = sourceTypeIcons[data.sourceType as keyof typeof sourceTypeIcons] || Factory;
          const totalProcurementValue = supplySourceData.reduce((sum, source) => sum + source.totalValue, 0);
          const percentage = totalProcurementValue > 0 ? (data.totalValue / totalProcurementValue * 100) : 0;
          
          return (
            <Card key={data.sourceType}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-4 w-4" style={{ color: sourceTypeColors[data.sourceType as keyof typeof sourceTypeColors] }} />
                      <Badge variant="secondary" className="capitalize">
                        {data.sourceType}
                      </Badge>
                    </div>
                    <p className="text-2xl font-bold">{data.procurementCount}</p>
                    <p className="text-xs text-muted-foreground">Procurements</p>
                    <p className="text-sm font-medium mt-1">
                      ETB {data.totalValue.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {percentage.toFixed(1)}% of total procurement
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {data.completionRate.toFixed(1)}% completion rate
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="breakdown">Source Breakdown</TabsTrigger>
          <TabsTrigger value="toplists">Top Lists</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Value by Source Type</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={supplySourceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sourceType" />
                    <YAxis />
                    <Tooltip formatter={(value: number) => [`ETB ${value.toLocaleString()}`, 'Total Value']} />
                    <Bar dataKey="totalValue" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Procurement Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={supplySourceData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="procurementCount"
                      label={({ sourceType, percent }) => `${sourceType} ${(percent * 100).toFixed(0)}%`}
                    >
                      {supplySourceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={sourceTypeColors[entry.sourceType as keyof typeof sourceTypeColors]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="breakdown" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Top Sources by Value</CardTitle>
              <CardDescription>Individual source performance ranking</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sourceBreakdown.slice(0, 10).map((source, index) => {
                  const Icon = sourceTypeIcons[source.sourceType as keyof typeof sourceTypeIcons] || Factory;
                  const totalProcurementValue = sourceBreakdown.reduce((sum, s) => sum + s.totalValue, 0);
                  const percentage = totalProcurementValue > 0 ? (source.totalValue / totalProcurementValue * 100) : 0;
                  
                  return (
                    <div key={`${source.sourceType}-${source.sourceName}`} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-sm font-medium">
                          {index + 1}
                        </div>
                        <Icon className="h-5 w-5" style={{ color: sourceTypeColors[source.sourceType as keyof typeof sourceTypeColors] }} />
                        <div>
                          <p className="font-medium">{source.sourceName}</p>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="capitalize">
                              {source.sourceType}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {source.procurementCount} procurement{source.procurementCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">ETB {source.totalValue.toLocaleString()}</p>
                        <p className="text-sm text-muted-foreground">
                          {percentage.toFixed(1)}% of total • {source.completionRate.toFixed(1)}% completion
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="toplists" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Top Sources by Value */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Sources by Value</CardTitle>
                <CardDescription>Highest performing sources by total procurement value</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {sourceBreakdown.slice(0, 5).map((source, index) => {
                    const Icon = sourceTypeIcons[source.sourceType as keyof typeof sourceTypeIcons] || Factory;
                    const totalProcurementValue = sourceBreakdown.reduce((sum, s) => sum + s.totalValue, 0);
                    const percentage = totalProcurementValue > 0 ? (source.totalValue / totalProcurementValue * 100) : 0;
                    
                    return (
                      <div key={`value-${source.sourceType}-${source.sourceName}`} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                          {index + 1}
                        </div>
                        <Icon className="h-4 w-4" style={{ color: sourceTypeColors[source.sourceType as keyof typeof sourceTypeColors] }} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{source.sourceName}</p>
                          <Badge variant="outline" className="capitalize text-xs">
                            {source.sourceType}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-sm">ETB {source.totalValue.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">{percentage.toFixed(1)}% • {source.procurementCount} orders</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Top Sources by Quantity */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Sources by Quantity</CardTitle>
                <CardDescription>Highest volume sources by total quantity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {sourceBreakdown
                    .sort((a, b) => b.totalQuantity - a.totalQuantity)
                    .slice(0, 5)
                    .map((source, index) => {
                      const Icon = sourceTypeIcons[source.sourceType as keyof typeof sourceTypeIcons] || Factory;
                      const totalQuantity = sourceBreakdown.reduce((sum, s) => sum + s.totalQuantity, 0);
                      const percentage = totalQuantity > 0 ? (source.totalQuantity / totalQuantity * 100) : 0;
                      
                      return (
                        <div key={`quantity-${source.sourceType}-${source.sourceName}`} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary text-secondary-foreground text-xs font-bold">
                            {index + 1}
                          </div>
                          <Icon className="h-4 w-4" style={{ color: sourceTypeColors[source.sourceType as keyof typeof sourceTypeColors] }} />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{source.sourceName}</p>
                            <Badge variant="outline" className="capitalize text-xs">
                              {source.sourceType}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm">{source.totalQuantity.toLocaleString()} kg</p>
                            <p className="text-xs text-muted-foreground">{percentage.toFixed(1)}% • {source.procurementCount} orders</p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* Top Sources by Completion Rate */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Sources by Completion Rate</CardTitle>
                <CardDescription>Most reliable sources by completion percentage</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {sourceBreakdown
                    .filter(source => source.procurementCount >= 2) // Only include sources with at least 2 procurements
                    .sort((a, b) => b.completionRate - a.completionRate)
                    .slice(0, 5)
                    .map((source, index) => {
                      const Icon = sourceTypeIcons[source.sourceType as keyof typeof sourceTypeIcons] || Factory;
                      return (
                        <div key={`completion-${source.sourceType}-${source.sourceName}`} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold">
                            {index + 1}
                          </div>
                          <Icon className="h-4 w-4" style={{ color: sourceTypeColors[source.sourceType as keyof typeof sourceTypeColors] }} />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{source.sourceName}</p>
                            <Badge variant="outline" className="capitalize text-xs">
                              {source.sourceType}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm text-green-600">{source.completionRate.toFixed(1)}%</p>
                            <p className="text-xs text-muted-foreground">{source.procurementCount} orders</p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* Top Sources by Order Count */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Sources by Order Count</CardTitle>
                <CardDescription>Most active sources by number of procurements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {sourceBreakdown
                    .sort((a, b) => b.procurementCount - a.procurementCount)
                    .slice(0, 5)
                    .map((source, index) => {
                      const Icon = sourceTypeIcons[source.sourceType as keyof typeof sourceTypeIcons] || Factory;
                      return (
                        <div key={`orders-${source.sourceType}-${source.sourceName}`} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-600 text-white text-xs font-bold">
                            {index + 1}
                          </div>
                          <Icon className="h-4 w-4" style={{ color: sourceTypeColors[source.sourceType as keyof typeof sourceTypeColors] }} />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{source.sourceName}</p>
                            <Badge variant="outline" className="capitalize text-xs">
                              {source.sourceType}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm text-orange-600">{source.procurementCount}</p>
                            <p className="text-xs text-muted-foreground">orders</p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Completion Rate by Source Type</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={supplySourceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sourceType" />
                    <YAxis />
                    <Tooltip formatter={(value: number) => [`${value.toFixed(1)}%`, 'Completion Rate']} />
                    <Bar dataKey="completionRate" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Rejection Rate by Source Type</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={supplySourceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sourceType" />
                    <YAxis />
                    <Tooltip formatter={(value: number) => [`${value.toFixed(1)}%`, 'Rejection Rate']} />
                    <Bar dataKey="rejectionRate" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupplySourceReport;